import React from "react";
import { sendCrashErrorDiscord } from "../api/sendErrorDiscord";
import { getAuthCentral } from "../config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "../models/DataFunctions";
import { captureToSentry } from "../config/sentry";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReload = this.handleReload.bind(this);
    this.handleGoBack = this.handleGoBack.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  async componentDidCatch(error, errorInfo) {
    const { prefix } = this.props;

    // Get user context from Firebase Auth and Firestore
    let userContext = null;
    try {
      const auth = getAuthCentral();
      const currentUser = auth.currentUser;

      if (currentUser) {
        const db = getFirestore();
        const userRef = doc(db, "user", currentUser.uid);
        const userSnapshot = await getDoc(checkDB(userRef));

        if (userSnapshot.exists()) {
          userContext = {
            email: currentUser.email || userSnapshot.get("email"),
            uid: currentUser.uid,
            org_id: userSnapshot.get("org_id"),
          };
          console.log("User context:", userContext);
        }
      }
    } catch (e) {
      console.error("Failed to get user context:", e);
    }

    // Extract more detailed error information
    const errorDetails = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      // Try to extract the real error location from stack
      fileName: error.fileName || this.extractFileFromStack(error.stack),
      lineNumber: error.lineNumber,
      columnNumber: error.columnNumber,
    };

    // Préparer le message d'erreur détaillé
    const errorMessage = {
      message: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      // Additional debugging info
      errorType: error.name,
      errorDetails: errorDetails,
      reactVersion: React.version,
      // Capture current route/path
      pathname: window.location.pathname,
      search: window.location.search,
    };

    console.error("ErrorBoundary caught an error:", errorMessage);
    console.error("Full error object:", error);
    console.error("Component stack:", errorInfo.componentStack);

    // Sentry : crash React avec le component stack et le contexte utilisateur.
    captureToSentry(error, {
      tags: { handler: "react_error_boundary" },
      extra: {
        componentStack: errorInfo?.componentStack,
        pathname: window.location.pathname,
      },
      user: userContext || undefined,
    });

    // Envoyer à Discord (crash errors) with user context
    sendCrashErrorDiscord(errorMessage, prefix, userContext);

    this.setState({
      error,
      errorInfo,
    });
  }

  extractFileFromStack(stack) {
    if (!stack) return null;
    // Try to extract filename from stack trace
    const match = stack.match(/https?:\/\/[^\s)]+\.js/);
    return match ? match[0] : null;
  }

  handleReload() {
    window.location.reload();
  }

  handleGoBack() {
    window.history.back();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 sm:p-8">
          <div className="max-w-2xl w-full">
            {/* Card principale */}
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 sm:p-12 space-y-6">
              {/* Icône d'erreur */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-red-100 rounded-full blur-xl opacity-60 animate-pulse"></div>
                  <div className="relative bg-red-50 rounded-full p-6">
                    <svg
                      className="w-16 h-16 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Titre */}
              <div className="text-center space-y-2">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  Oups ! Une erreur est survenue
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Nous sommes désolés pour ce désagrément. Notre équipe a été automatiquement notifiée et travaille déjà sur une solution.
                </p>
              </div>

              {/* Bouton d'action */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={this.handleReload}
                  className="flex-1 px-8 py-4 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 ease-out cursor-pointer"
                >
                  Recharger la page
                </button>
                <button
                  type="button"
                  onClick={this.handleGoBack}
                  className="flex-1 px-8 py-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 active:scale-95 transition-all duration-200 ease-out cursor-pointer"
                >
                  Retour
                </button>
              </div>

              {/* Info supplémentaire */}
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500 text-center">
                  Si le problème persiste, n'hésitez pas à nous contacter
                </p>
              </div>

              {/* Détails de l'erreur (dev only) */}
              {process.env.NODE_ENV === "development" && (
                <details className="mt-6">
                  <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200 transition-colors">
                    🔧 Détails techniques (dev only)
                  </summary>
                  <div className="mt-3 bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-96 font-mono text-xs">
                    <div className="text-red-400 font-semibold mb-2">Error:</div>
                    <pre className="whitespace-pre-wrap break-words">
                      {this.state.error && this.state.error.toString()}
                    </pre>
                    {this.state.errorInfo && (
                      <>
                        <div className="text-yellow-400 font-semibold mt-4 mb-2">
                          Component Stack:
                        </div>
                        <pre className="whitespace-pre-wrap break-words text-gray-300">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
